import { owedAfter } from "@pax/agent-sdk/owed";
import type { AiCall } from "@pax/api-contract/ai/calls/v1alpha1";
import type { Scorecard } from "@pax/api-contract/evals/v1alpha1";
import type { GameSpan } from "@pax/api-contract/traces/v1alpha1";
import { GEN_AI, PAX } from "@pax/telemetry/attributes";
import type { CommittedDelta, DeltaTarget, OperationType } from "@pax/world";
import { costOf } from "./model-call";

/**
 * Lays a game out for the debugging timeline: the activities its actors ran, the deltas it
 * committed in groups, its rounds, and a time scale that skips the stretches where nothing ran.
 * An activity is one trace whose root span carries `pax.activity.kind`; the telemetry docs'
 * Activities section defines every attribute read here.
 */

const DRAWN_ROLES = ["gameMaster", "player", "grader"] as const;
type AgentRole = (typeof DRAWN_ROLES)[number];

const OUTCOMES = ["committed", "idle", "fallback", "failed"] as const;
export type ActivityOutcome = (typeof OUTCOMES)[number];

const COMMIT_SPAN_NAME = "game_session.commit";
const TURN_SPAN_NAME = "agent.conversation.turn";
/** Spans an activity's tree shows before asking for everything, such as token checks. */
const NOTABLE_SPAN_PREFIXES = [
  "agent.",
  "eval.",
  "chat ",
  "execute_tool",
  "game.",
  "game_session.propose",
  "game_session.commit",
  "game_session.submit_scorecard",
  "grader.",
];

export interface RevisionRange {
  from: number;
  through: number;
}

/** One agent doing one unit of work, and everything its trace recorded. */
export interface Activity {
  traceId: string;
  root: GameSpan;
  role: AgentRole;
  agentId: string;
  agentName: string | null;
  kind: string;
  label: string;
  key: string | null;
  /** The deltas it answers, by revision, oldest first. */
  answering: number[];
  readThroughRevision: number | null;
  outcome: ActivityOutcome | null;
  /** The deltas it committed, by revision, oldest first. */
  resultedIn: number[];
  /** The deltas each of its model calls read for the first time, with the request that read them. */
  firstSeen: { revisions: number[]; request: GameSpan }[];
  startMs: number;
  endMs: number;
  /** Every other span in the trace, from any service, in the order they started. */
  spans: GameSpan[];
  modelCalls: AiCall[];
  toolSpans: GameSpan[];
  /** The steps it took in the agent's conversations, in the order it took them. */
  turns: ConversationTurn[];
  costUsd: number;
}

/** One context window as an agent keeps it, across the activities that use it and its compactions. */
export interface ConversationRef {
  id: string;
  kind: string;
  label: string;
}

/** One step an activity took in a conversation: its `agent.conversation.turn` span. */
export interface ConversationTurn {
  conversation: ConversationRef;
  /** How many times the conversation had compacted before the turn. */
  generation: number;
  span: GameSpan;
  startMs: number;
  endMs: number;
}

export type Finding = Scorecard["sections"][number]["findings"][number] & { sectionKey: string };

/** Deltas the game server committed together, and the activity that proposed them. */
export interface CommitGroup {
  deltas: CommittedDelta[];
  firstRevision: number;
  lastRevision: number;
  committedAtMs: number;
  commitSpan: GameSpan | null;
  activity: Activity | null;
  findings: Finding[];
}

interface RoundBand {
  /** Zero for setting up the world, then one per round of play. */
  round: number;
  date: string | null;
  startMs: number;
  endMs: number;
  /** When the jump that ends the round started. */
  jumpStartedMs: number | null;
}

export interface GameActivity {
  activities: Activity[];
  commits: CommitGroup[];
  rounds: RoundBand[];
  /** Revisions the game master still owes an answer that no activity is answering. */
  unanswered: ReadonlySet<number>;
}

export function readGameActivity({
  record,
  spans,
  modelCalls,
  scorecard,
}: {
  record: readonly CommittedDelta[];
  spans: readonly GameSpan[];
  modelCalls: readonly AiCall[];
  scorecard: Scorecard | null;
}): GameActivity {
  const activities = activitiesOf(spans, modelCalls);
  const activityByTrace = new Map(activities.map(activity => [activity.traceId, activity]));
  const commitSpanByRevision = new Map<number, GameSpan>();
  for (const span of spans) {
    const revision = span.name === COMMIT_SPAN_NAME ? revisionOf(span, PAX.revision) : null;
    if (revision) commitSpanByRevision.set(revision, span);
  }
  const findings = (scorecard?.sections ?? []).flatMap(section =>
    section.findings.map(finding => ({ ...finding, sectionKey: section.key })),
  );
  const commits = groupByCommit(record).map((deltas): CommitGroup => {
    const firstRevision = deltas[0]?.revision ?? 0;
    const lastRevision = deltas.at(-1)?.revision ?? firstRevision;
    const commitSpan = commitSpanByRevision.get(lastRevision) ?? null;
    return {
      deltas,
      firstRevision,
      lastRevision,
      committedAtMs: Date.parse(deltas[0]?.committedAt ?? ""),
      commitSpan,
      activity: commitSpan
        ? (activityByTrace.get(commitSpan.traceId) ?? null)
        : (activities.find(activity => activity.resultedIn.includes(lastRevision)) ?? null),
      findings: findings.filter(
        finding => finding.revision >= firstRevision && finding.revision <= lastRevision,
      ),
    };
  });
  const answering = new Set(activities.flatMap(activity => activity.answering));
  const unanswered = new Set(
    owedAfter(record)
      .flatMap(work => work.deltas.map(delta => delta.revision))
      .filter(revision => !answering.has(revision)),
  );
  return { activities, commits, rounds: roundsOf(commits), unanswered };
}

function activitiesOf(spans: readonly GameSpan[], modelCalls: readonly AiCall[]): Activity[] {
  const spansByTrace = groupBy(spans, span => span.traceId);
  const callsByTrace = groupBy(modelCalls, call => call.traceId);
  const activities: Activity[] = [];
  for (const [traceId, traceSpans] of spansByTrace) {
    const root = traceSpans.find(
      span => span.parentSpanId === null && span.attributes[PAX.activityKind],
    );
    const role = root?.resource[PAX.agentRole];
    if (!root || !isDrawnRole(role)) continue;
    const attribute = (name: string) => root.attributes[name] ?? null;
    const others = traceSpans
      .filter(span => span !== root)
      .sort((left, right) => left.startedAt.localeCompare(right.startedAt));
    const calls = [...(callsByTrace.get(traceId) ?? [])].sort((left, right) =>
      left.receivedAt.localeCompare(right.receivedAt),
    );
    const outcome = attribute(PAX.activityOutcome);
    const kind = attribute(PAX.activityKind) ?? "";
    const startMs = Date.parse(root.startedAt);
    activities.push({
      traceId,
      root,
      role,
      agentId: root.resource[PAX.agentId] ?? "",
      agentName: attribute(GEN_AI.agentName),
      kind,
      label: attribute(PAX.activityLabel) ?? kind,
      key: attribute(PAX.activityKey),
      answering: revisionsOf(root.attributes[PAX.activityAnswering]),
      readThroughRevision: revisionOf(root, PAX.activityReadThroughRevision),
      outcome: OUTCOMES.find(known => known === outcome) ?? null,
      resultedIn: revisionsOf(root.attributes[PAX.activityResultedIn]),
      firstSeen: others.flatMap(span => {
        const revisions = revisionsOf(span.attributes[PAX.contextNewlyVisible]);
        return revisions.length ? [{ revisions, request: span }] : [];
      }),
      startMs,
      endMs: startMs + root.durationMs,
      spans: others,
      modelCalls: calls,
      toolSpans: others.filter(isToolSpan),
      turns: others.flatMap(turnOf),
      costUsd: calls.reduce((sum, call) => sum + (costOf(call) ?? 0), 0),
    });
  }
  return activities.sort((left, right) => left.startMs - right.startMs);
}

function turnOf(span: GameSpan): ConversationTurn[] {
  const id = span.attributes[GEN_AI.conversationId];
  if (span.name !== TURN_SPAN_NAME || !id) return [];
  const startMs = Date.parse(span.startedAt);
  return [
    {
      conversation: {
        id,
        kind: span.attributes[PAX.conversationKind] ?? "",
        label: span.attributes[PAX.conversationLabel] ?? id,
      },
      generation: Number(span.attributes[PAX.conversationGeneration] ?? 0),
      span,
      startMs,
      endMs: startMs + span.durationMs,
    },
  ];
}

/** The conversations some activities took turns in, in the order each was first used. */
export function conversationsOf(activities: readonly Activity[]): ConversationRef[] {
  const byId = new Map<string, ConversationRef>();
  for (const activity of activities)
    for (const turn of activity.turns)
      if (!byId.has(turn.conversation.id)) byId.set(turn.conversation.id, turn.conversation);
  return [...byId.values()];
}

const isDrawnRole = (role: string | undefined): role is AgentRole =>
  DRAWN_ROLES.some(drawn => drawn === role);

/** An array attribute, which ClickHouse keeps as JSON text. */
function arrayOf(value: string | undefined): unknown[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const revisionsOf = (value: string | undefined) =>
  arrayOf(value)
    .map(Number)
    .filter(revision => Number.isInteger(revision) && revision > 0)
    .sort((left, right) => left - right);

/** The commits holding any of these revisions. */
export const commitsHolding = (commits: readonly CommitGroup[], revisions: readonly number[]) =>
  commits.filter(commit =>
    revisions.some(revision => commit.firstRevision <= revision && revision <= commit.lastRevision),
  );

/** Revisions in words, runs of consecutive ones joined, such as "rev 4–6, 9". */
export function revisionList(revisions: readonly number[]): string {
  const runs: [number, number][] = [];
  for (const revision of revisions) {
    const run = runs.at(-1);
    if (run && revision === run[1] + 1) run[1] = revision;
    else runs.push([revision, revision]);
  }
  return `rev ${runs.map(([from, through]) => (from === through ? from : `${from}–${through}`)).join(", ")}`;
}

/** The deltas one model call read for the first time, as the agent's request for it said. */
export function firstSeenIn(activity: Activity, call: AiCall): number[] {
  const parents = new Map(activity.spans.map(span => [span.spanId, span.parentSpanId]));
  for (let spanId = call.parentSpanId; spanId; spanId = parents.get(spanId) ?? null) {
    const seen = activity.firstSeen.find(({ request }) => request.spanId === spanId);
    if (seen) return seen.revisions;
  }
  return [];
}

function revisionOf(span: GameSpan, attribute: string): number | null {
  const revision = Number(span.attributes[attribute]);
  return Number.isInteger(revision) && revision > 0 ? revision : null;
}

function groupBy<T>(items: readonly T[], keyOf: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  return groups;
}

function groupByCommit(record: readonly CommittedDelta[]): CommittedDelta[][] {
  const groups: CommittedDelta[][] = [];
  for (const delta of record) {
    const last = groups.at(-1);
    if (last && last[0]?.committedAt === delta.committedAt) last.push(delta);
    else groups.push([delta]);
  }
  return groups;
}

export const commitContaining = (commits: readonly CommitGroup[], revision: number) =>
  commits.find(commit => commit.firstRevision <= revision && revision <= commit.lastRevision);

/** Commits that share at least one revision with a range. */
export const commitsWithin = (commits: readonly CommitGroup[], range: RevisionRange) =>
  commits.filter(
    commit => commit.lastRevision >= range.from && commit.firstRevision <= range.through,
  );

/* ── Rounds ─────────────────────────────────────────────────────────── */

/** Play starts with the first delta a player or the Game Master wrote, or the first jump. */
const isPlay = (delta: CommittedDelta) =>
  delta.actor.type !== "system" || delta.operation.type === "jump.started";

/** Whether a delta reaches the spectator, whose labels date the rounds. */
const reachesSpectator = (target: DeltaTarget) =>
  target.type === "activeViews" ||
  (target.type === "perspectives" && target.perspectives.some(ref => ref.type === "spectator"));

/**
 * Setup until play starts, then a new round each time a jump completes, dated with the spectator's
 * label where it begins. Each band runs until the next one starts.
 */
function roundsOf(commits: readonly CommitGroup[]): RoundBand[] {
  const rounds: RoundBand[] = [];
  const labels = new Map<number, string>();
  let tick = 0;
  let playing = false;
  const open = (startMs: number) => {
    const previous = rounds.at(-1);
    if (previous) previous.endMs = startMs;
    rounds.push({
      round: rounds.length,
      date: labels.get(tick) ?? null,
      startMs,
      endMs: Number.POSITIVE_INFINITY,
      jumpStartedMs: null,
    });
  };
  open(Number.NEGATIVE_INFINITY);
  let commitsInRound = 0;
  for (const commit of commits) {
    if (!playing && commit.deltas.some(isPlay)) {
      playing = true;
      open(commit.committedAtMs);
      commitsInRound = 0;
    }
    commitsInRound += 1;
    let roundEnds = false;
    for (const { operation, target } of commit.deltas) {
      if (operation.type === "world.started") tick = operation.tick;
      if (
        reachesSpectator(target) &&
        (operation.type === "timeline.extended" || operation.type === "timeline.clarified")
      ) {
        const write =
          operation.type === "timeline.extended" ? operation.extension : operation.clarification;
        for (const label of write.labels ?? []) labels.set(label.tick, label.date);
      }
      if (operation.type === "jump.completed") {
        roundEnds = true;
        tick = operation.tick;
      }
      const current = rounds.at(-1);
      if (operation.type === "jump.started" && current)
        current.jumpStartedMs ??= commit.committedAtMs;
    }
    if (roundEnds) {
      open(commit.committedAtMs);
      commitsInRound = 0;
    }
  }
  if (rounds.length > 1 && commitsInRound === 0) {
    rounds.pop();
    const last = rounds.at(-1);
    if (last) last.endMs = Number.POSITIVE_INFINITY;
  }
  return rounds;
}

/* ── Lanes ──────────────────────────────────────────────────────────── */

/** One row of the timeline: the Game Master, each synthetic player, or the grader. */
export interface Lane {
  key: string;
  name: string;
  role: AgentRole;
  activities: Activity[];
  /** The conversations its activities took turns in, each of which can open as a row of its own. */
  conversations: ConversationRef[];
}

const LANE_ORDER: Record<AgentRole, number> = { gameMaster: 0, player: 1, grader: 2 };

/** The Game Master's lane first, even when it was replaced mid-game, then players, then the grader. */
export function lanesOf(activities: readonly Activity[], gameMasterName: string): Lane[] {
  const lanes = new Map<string, Lane>();
  for (const activity of activities) {
    const key = activity.role === "player" ? `player:${activity.agentId}` : activity.role;
    const lane = lanes.get(key);
    if (lane) {
      lane.activities.push(activity);
      if (activity.role === "player" && activity.agentName) lane.name = activity.agentName;
      continue;
    }
    lanes.set(key, {
      key,
      role: activity.role,
      name:
        activity.role === "gameMaster"
          ? gameMasterName
          : activity.role === "grader"
            ? "Grader"
            : (activity.agentName ?? "Player"),
      activities: [activity],
      conversations: [],
    });
  }
  for (const lane of lanes.values()) lane.conversations = conversationsOf(lane.activities);
  return [...lanes.values()].sort((left, right) => LANE_ORDER[left.role] - LANE_ORDER[right.role]);
}

/** Each item in the lowest row where it overlaps nothing, and how many rows that took. */
export function packRows<T>(
  items: readonly T[],
  extentOf: (item: T) => { left: number; right: number },
) {
  const rowEnds: number[] = [];
  const placed = items
    .map(item => ({ item, ...extentOf(item) }))
    .sort((a, b) => a.left - b.left)
    .map(entry => {
      let row = rowEnds.findIndex(end => end <= entry.left);
      if (row === -1) row = rowEnds.push(entry.right) - 1;
      else rowEnds[row] = entry.right;
      return { ...entry, row };
    });
  return { placed, rowCount: Math.max(1, rowEnds.length) };
}

/* ── Time scale ─────────────────────────────────────────────────────── */

/** A quiet stretch longer than this collapses into a narrow break. */
const SKIPPED_GAP_MS = 5000;
/** Room kept on each side of a busy stretch so its first and last marks are not on its edge. */
const STRETCH_MARGIN_MS = 1000;
const BREAK_WIDTH = 40;
/** The narrowest break its sideways label fits in. Narrower breaks go unlabeled. */
export const LABELED_BREAK_WIDTH = 14;
/** The most of the track breaks may take up when each is kept wide enough to label. */
const LABELED_BREAKS_SHARE = 0.5;

interface TimeBreak {
  /** When the quiet stretch it stands for began. */
  startMs: number;
  left: number;
  width: number;
  skippedMs: number;
}

/** A pixel named by the moment drawn nearest it and how far past that moment it lies. */
export interface TimeAnchor {
  ms: number;
  pastPx: number;
}

export interface TimeScale {
  x: (ms: number) => number;
  /** The pixel's anchor, which `x(anchor.ms) + anchor.pastPx` turns back into the same pixel. */
  anchorAt: (x: number) => TimeAnchor;
  breaks: TimeBreak[];
}

/**
 * Real time across `width` pixels, except that quiet stretches longer than a few seconds shrink to
 * a fixed break, so a long wait for a model takes no more room than a short one. Breaks are sized
 * for `unzoomedWidth`, so they keep their width at every zoom.
 */
export function collapsedTimeScale(
  intervals: readonly { startMs: number; endMs: number }[],
  width: number,
  unzoomedWidth = width,
): TimeScale {
  const stretches: { startMs: number; endMs: number }[] = [];
  const sorted = intervals
    .filter(interval => Number.isFinite(interval.startMs) && Number.isFinite(interval.endMs))
    .sort((left, right) => left.startMs - right.startMs);
  for (const interval of sorted) {
    const last = stretches.at(-1);
    if (last && interval.startMs - last.endMs <= SKIPPED_GAP_MS)
      last.endMs = Math.max(last.endMs, interval.endMs);
    else stretches.push({ ...interval });
  }
  if (stretches.length === 0)
    return { x: () => 0, anchorAt: at => ({ ms: 0, pastPx: at }), breaks: [] };

  const breakCount = Math.max(1, stretches.length - 1);
  const evenShare = (unzoomedWidth * 0.25) / breakCount;
  const labeledFits = breakCount * LABELED_BREAK_WIDTH <= unzoomedWidth * LABELED_BREAKS_SHARE;
  const breakWidth = Math.min(
    BREAK_WIDTH,
    labeledFits ? Math.max(LABELED_BREAK_WIDTH, evenShare) : evenShare,
  );
  const shownMs = stretches.reduce(
    (sum, stretch) => sum + stretch.endMs - stretch.startMs + 2 * STRETCH_MARGIN_MS,
    0,
  );
  const pixelsPerMs = Math.max(0, width - breakWidth * (stretches.length - 1)) / shownMs;
  let left = 0;
  const placed = stretches.map(stretch => {
    const fromMs = stretch.startMs - STRETCH_MARGIN_MS;
    const toMs = stretch.endMs + STRETCH_MARGIN_MS;
    const at = { fromMs, toMs, left, right: left + (toMs - fromMs) * pixelsPerMs };
    left = at.right + breakWidth;
    return at;
  });
  const breaks = placed.slice(1).map((stretch, index) => ({
    startMs: placed[index]?.toMs ?? 0,
    left: stretch.left - breakWidth,
    width: breakWidth,
    skippedMs:
      (stretches[index + 1]?.startMs ?? 0) - (stretches[index]?.endMs ?? 0) - STRETCH_MARGIN_MS * 2,
  }));
  const x = (ms: number) => {
    for (const [index, stretch] of placed.entries()) {
      if (ms < stretch.fromMs) return index === 0 ? 0 : stretch.left - breakWidth / 2;
      if (ms <= stretch.toMs) return stretch.left + (ms - stretch.fromMs) * pixelsPerMs;
    }
    return width;
  };
  const anchorAt = (at: number): TimeAnchor => {
    for (const [index, stretch] of placed.entries()) {
      const previous = placed[index - 1];
      if (at < stretch.left)
        return previous
          ? { ms: previous.toMs, pastPx: at - previous.right }
          : { ms: stretch.fromMs, pastPx: at - stretch.left };
      if (at <= stretch.right)
        return {
          ms: stretch.fromMs + (pixelsPerMs > 0 ? (at - stretch.left) / pixelsPerMs : 0),
          pastPx: 0,
        };
    }
    const last = placed.at(-1);
    return { ms: last?.toMs ?? 0, pastPx: at - (last?.right ?? 0) };
  };
  return { x, anchorAt, breaks };
}

/* ── Commit clusters ────────────────────────────────────────────────── */

/** Commits drawn so close together that they share one circle. */
export interface CommitCluster {
  commits: CommitGroup[];
  x: number;
  deltaCount: number;
}

export function clusterCommits(
  commits: readonly CommitGroup[],
  x: (ms: number) => number,
  diameter: number,
): CommitCluster[] {
  const clusters: (CommitCluster & { firstX: number; lastX: number })[] = [];
  for (const commit of commits) {
    const at = x(commit.committedAtMs);
    const last = clusters.at(-1);
    if (last && at - last.firstX < diameter) {
      last.commits.push(commit);
      last.lastX = at;
      last.deltaCount += commit.deltas.length;
    } else {
      clusters.push({
        commits: [commit],
        firstX: at,
        lastX: at,
        x: at,
        deltaCount: commit.deltas.length,
      });
    }
  }
  return clusters.map(({ firstX, lastX, ...cluster }) => ({
    ...cluster,
    x: (firstX + lastX) / 2,
  }));
}

/* ── Words for deltas ───────────────────────────────────────────────── */

export type CommitKind =
  | "jump"
  | "action"
  | "chat"
  | "event"
  | "map"
  | "input"
  | "perspective"
  | "other";

const KIND_BY_OPERATION_PREFIX: [string, CommitKind][] = [
  ["jump.", "jump"],
  ["action.", "action"],
  ["thread.", "chat"],
  ["advisor.", "chat"],
  ["timeline.", "event"],
  ["scene.", "event"],
  ["world.", "event"],
  ["input.", "input"],
  ["perspective", "perspective"],
  ["distributionList.", "perspective"],
];

const kindOfOperation = (type: OperationType): CommitKind =>
  KIND_BY_OPERATION_PREFIX.find(([prefix]) => type.startsWith(prefix))?.[1] ?? "other";

/** What a set of deltas mostly did: a jump or player action when there is one, else the commonest kind. */
export function commitKindOf(deltas: readonly CommittedDelta[]): CommitKind {
  const counts = new Map<CommitKind, number>();
  for (const { operation } of deltas) {
    const kind = kindOfOperation(operation.type);
    counts.set(kind, (counts.get(kind) ?? 0) + 1);
  }
  if (counts.has("jump")) return "jump";
  if (counts.has("action")) return "action";
  return [...counts].sort((left, right) => right[1] - left[1])[0]?.[0] ?? "other";
}

/** One line about what a group of deltas did, in the words a reader would use. */
export function describeDeltas(deltas: readonly CommittedDelta[]): string {
  const actor = deltas[0]?.actor;
  for (const { operation } of deltas) {
    if (operation.type === "action.submitted") return `Player action: “${operation.action.text}”`;
    if (operation.type === "jump.requested")
      return `Player asked to jump${operation.guidance ? `: ${operation.guidance}` : ""}`;
  }
  const who =
    actor?.type === "gameMaster"
      ? "Game Master"
      : actor?.type === "perspective" || actor?.type === "user"
        ? "Player"
        : "System";
  return `${who} · ${operationCounts(deltas)}`;
}

function operationCounts(deltas: readonly CommittedDelta[]): string {
  const counts = new Map<string, number>();
  for (const { operation } of deltas)
    counts.set(operation.type, (counts.get(operation.type) ?? 0) + 1);
  const shown = [...counts]
    .sort((left, right) => right[1] - left[1])
    .map(([type, count]) => (count === 1 ? type : `${count} ${type}`));
  return shown.length > 4 ? `${shown.slice(0, 4).join(", ")} and more` : shown.join(", ");
}

/* ── Spans ──────────────────────────────────────────────────────────── */

export const isToolSpan = (span: GameSpan) =>
  span.attributes[GEN_AI.operationName] === "execute_tool";

export const toolNameOf = (span: GameSpan) => span.attributes[GEN_AI.toolName] ?? span.name;

/** The model's id for the call a tool span ran, which its reply and the next prompt both name. */
export const toolCallIdOf = (span: GameSpan) => span.attributes[GEN_AI.toolCallId];

/** What the tool answered, as its span kept it. */
export const toolResultOf = (span: GameSpan) => span.attributes[GEN_AI.toolCallResult];

export const spanEndMs = (span: GameSpan) => Date.parse(span.startedAt) + span.durationMs;

/** A span and the spans inside it, in the order they started. */
export interface SpanNode {
  span: GameSpan;
  children: SpanNode[];
}

/**
 * An activity's spans as a tree under its root. Without `everything`, plumbing such as token
 * checks is left out and whatever notable ran inside it moves up to the nearest span shown.
 */
export function spanTree(activity: Activity, everything: boolean): SpanNode {
  const byId = new Map([activity.root, ...activity.spans].map(span => [span.spanId, span]));
  const shown = (span: GameSpan) =>
    everything ||
    span.status === "error" ||
    NOTABLE_SPAN_PREFIXES.some(prefix => span.name.startsWith(prefix));
  const nodes = new Map<string, SpanNode>(
    [activity.root, ...activity.spans.filter(shown)].map(span => [
      span.spanId,
      { span, children: [] },
    ]),
  );
  const root = nodes.get(activity.root.spanId) as SpanNode;
  const nearestShownAncestor = (span: GameSpan): SpanNode => {
    for (let id = span.parentSpanId; id; id = byId.get(id)?.parentSpanId ?? null) {
      const node = nodes.get(id);
      if (node) return node;
      if (!byId.has(id)) break;
    }
    return root;
  };
  for (const span of activity.spans) {
    const node = nodes.get(span.spanId);
    if (node) nearestShownAncestor(span).children.push(node);
  }
  return root;
}

/** The tool spans that ran after a model call and before the activity's next one: what it asked for. */
export function toolSpansAfter(activity: Activity, call: AiCall): GameSpan[] {
  const next = activity.modelCalls[activity.modelCalls.indexOf(call) + 1];
  const from = Date.parse(call.endedAt);
  const until = next ? Date.parse(next.receivedAt) : Number.POSITIVE_INFINITY;
  return activity.toolSpans.filter(span => {
    const at = Date.parse(span.startedAt);
    return at >= from && at <= until;
  });
}

/** The model call whose answer asked for a tool span: the activity's last call to end before it. */
export function modelCallBefore(activity: Activity, span: GameSpan): AiCall | undefined {
  const at = Date.parse(span.startedAt);
  return [...activity.modelCalls].reverse().find(call => Date.parse(call.endedAt) <= at);
}
